import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  Card, Table, Button, Tag, Modal, Form, Input, Select, DatePicker, Row, Col,
  Popconfirm, Tooltip, Typography, Descriptions, EmptyState, message, TimePicker, cn, type TagTone,
} from '../../ui';
import { Plus, Check, X, Undo2, Info } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { formatIstDate, formatIstTime } from '../../lib/time'; // human dates: the tables printed "2026-09-11" (E2E 15 Sep)
import { useFocusRow } from '../../hooks/useFocusRow';
import { RoleBadge } from '../../components/RoleBadge';
import { UserLink } from '../../components/EntityLinks';
import { CodeChip } from '../../ui/CodeChip';
import { TerritoryNodeChips } from '../../components/TerritoryChips';
import { useStaffTerritories } from '../../lib/useStaffTerritories';

export const REG_TYPE_LABEL: Record<string, string> = {
  MISSING_CHECK_IN: 'Forgot check-in', MISSING_CHECK_OUT: 'Forgot check-out', MISSING_BOTH: 'Forgot both',
};
export const REG_STATUS_COLOR: Record<string, TagTone> = {
  PENDING: 'gold', APPROVED: 'green', REJECTED: 'red', CANCELLED: 'neutral',
};
const DAY_TYPE = { PRESENT: 'green', HALF_DAY: 'gold' } as Record<string, TagTone>;

const requestedCell = (r: any) => (
  <div style={{ fontSize: 12 }}>
    {r.requestedCheckInAt && <div>In: {formatIstTime(r.requestedCheckInAt)}</div>}
    {r.requestedCheckOutAt && <div>Out: {formatIstTime(r.requestedCheckOutAt)}</div>}
    <Tag tone={DAY_TYPE[r.requestedStatus] ?? 'neutral'}>{r.requestedStatus}</Tag>
  </div>
);

// ── SP side: raise + track your own requests ──────────────────────────────────
export function MyRegularizationsCard() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: mine, isLoading } = useQuery({
    queryKey: ['reg-mine'],
    queryFn: () => request<any[]>({ url: '/attendance-regularizations/mine' }),
  });
  /* A decision on one of these requests links here with ?focus=<id> (DEF-071): tint that row and bring it into view.
     The card sits below the fold on My Field Day, and the hook's own scroll is cancelled when it clears `focus` from the
     URL in the same moment after an in-app navigation (seen from the bell) — so the card scrolls to the row itself,
     once, with nothing to cancel it. */
  const focus = useFocusRow(mine);
  const focusedRegId = focus.focused?.id;
  const regTableRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusedRegId == null) return;
    requestAnimationFrame(() => regTableRef.current?.querySelector(`[data-row-key="${CSS.escape(String(focusedRegId))}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
  }, [focusedRegId]);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['reg-mine'] });
    qc.invalidateQueries({ queryKey: ['my-day'] });
  };
  const cancelMut = useMutation({
    mutationFn: (id: string) => request({ url: `/attendance-regularizations/${id}/cancel`, method: 'POST' }),
    onSuccess: () => { message.success('Request withdrawn'); refresh(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const cols = [
    /* The request's own number — what the approvals hub, the bell and the reversal register all call
       it. Without it the only way to name a request was "the one for the 10th", and a rep can have
       several. Requests raised before the REG series existed show a dash. */
    { title: 'Request', dataIndex: 'docNo', width: 150, render: (v: string | null) => (v ? <CodeChip>{v}</CodeChip> : <span className="text-subtle">—</span>) },
    { title: 'Date', dataIndex: 'logDate', width: 110, render: (v: string) => <span className="whitespace-nowrap">{formatIstDate(v)}</span> },
    { title: 'Missed', dataIndex: 'type', render: (v: string) => REG_TYPE_LABEL[v] ?? v },
    { title: 'Requested', render: (_: any, r: any) => requestedCell(r) },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true },
    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag tone={REG_STATUS_COLOR[v] ?? 'neutral'}>{v}</Tag> },
    { title: 'Reviewer', render: (_: any, r: any) => r.reviewedByName ?? '—' },
    { title: 'Note', dataIndex: 'reviewNote', render: (v: any) => v ?? '—', ellipsis: true },
    {
      title: '', width: 90, render: (_: any, r: any) => r.status === 'PENDING'
        ? (
          <Tooltip label="Withdraw this request">
            <Popconfirm title="Withdraw this request?" onConfirm={() => cancelMut.mutate(String(r.id))}>
              <Button size="sm" variant="text" aria-label="Withdraw this request" icon={<Undo2 size={16} className="text-danger-600 dark:text-danger-500" />} />
            </Popconfirm>
          </Tooltip>
        )
        : null,
    },
  ];

  return (
    <Card title="Attendance regularization"
      extra={<Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setOpen(true)}>Request</Button>}>
      <Typography.Paragraph type="secondary" style={{ marginTop: -4, fontSize: 12 }}>
        Forgot to punch in or out? Request your manager to mark that day on your behalf (last 7 days).
      </Typography.Paragraph>
      <div ref={regTableRef}>
        <Table rowKey="id" size="small" loading={isLoading} dataSource={mine ?? []} columns={cols as any} rowClassName={focus.rowClassName}
          pagination={{ defaultPageSize: 5, showSizeChanger: true }}
          locale={{ emptyText: <EmptyState title="No regularization requests" /> }} />
      </div>
      <RequestRegularizationModal open={open} onClose={() => { setOpen(false); refresh(); }} />
    </Card>
  );
}

const DEFAULT_REG_TYPE = 'MISSING_BOTH';

/** One fact of the request being written: sentence-case label over a full-contrast value, on its own tint. */
function RegStat({ label, value, hint, tint }: { label: string; value: React.ReactNode; hint?: string; tint: string }) {
  return (
    <div className={cn('min-w-0 rounded-card border border-line p-2.5 shadow-e1', tint)}>
      <div className="type-caption text-subtle">{label}</div>
      <div className="mt-0.5 type-body-strong wrap-break-word text-content">{value}</div>
      {hint && <div className="type-caption text-muted">{hint}</div>}
    </div>
  );
}

function RequestRegularizationModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  /*
   * Which times to ask for follows the reason picked — held here, not read with `Form.useWatch` (DEF-070, 15 Sep).
   * This component renders BEFORE the <Form> inside the modal mounts and seeds its initialValues, and seeding tells no
   * watcher: the watch read `undefined`, so the dialog opened on "Forgot both" with no time field at all until some
   * other field changed. The value starts on the form's default, follows the picker through onValuesChange, and goes
   * back to the default whenever the dialog closes — as the form does when it next mounts.
   */
  const [type, setType] = useState<string>(DEFAULT_REG_TYPE);
  // The same reason the type is held here (DEF-070): the summary strip below has to read the day, the day type and the
  // times the moment they change, and a watcher misses the form's own seeding.
  const [day, setDay] = useState<Dayjs | null>(dayjs().subtract(1, 'day'));
  const [dayType, setDayType] = useState<string>('PRESENT');
  const [times, setTimes] = useState<{ checkInTime?: Dayjs | null; checkOutTime?: Dayjs | null }>({});
  useEffect(() => {
    if (!open) { setType(DEFAULT_REG_TYPE); setDay(dayjs().subtract(1, 'day')); setDayType('PRESENT'); setTimes({}); }
  }, [open]);
  const needIn = type === 'MISSING_CHECK_IN' || type === 'MISSING_BOTH';
  const needOut = type === 'MISSING_CHECK_OUT' || type === 'MISSING_BOTH';

  const save = useConfirmedMutation({ title: "Send this regularisation request?", content: "It goes to your manager to approve.", okText: "Yes, send it" }, {
    mutationFn: (vals: any) => {
      const combine = (t: any) => t ? dayjs(vals.logDate).hour(t.hour()).minute(t.minute()).second(0).toISOString() : undefined;
      const isIn = vals.type === 'MISSING_CHECK_IN' || vals.type === 'MISSING_BOTH';
      const isOut = vals.type === 'MISSING_CHECK_OUT' || vals.type === 'MISSING_BOTH';
      return request({
        url: '/attendance-regularizations', method: 'POST', data: {
          logDate: vals.logDate.format('YYYY-MM-DD'),
          type: vals.type,
          checkInAt: isIn ? combine(vals.checkInTime) : undefined,
          checkOutAt: isOut ? combine(vals.checkOutTime) : undefined,
          status: vals.status,
          reason: vals.reason,
        },
      });
    },
    onSuccess: () => { message.success('Requested — your manager will review it'); form.resetFields(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to submit'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Request attendance regularization"
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Submit request</Button>
      </>}>
      {/* WHAT THIS ASKS FOR, before the fields: the day being fixed, what is missing on it, and what it becomes —
          so the rep checks their own request at a glance instead of re-reading four controls. */}
      <div className="mb-3 flex items-start gap-2 rounded-control border border-sky-500/25 bg-sky-500/10 px-3.5 py-2.5 text-sm text-content">
        <Info size={16} className="mt-0.5 shrink-0 text-sky-600 dark:text-sky-400" />
        <span>
          Only the last 7 days can be regularized — older days stay <b>Absent</b>. Your manager sees the day, the times
          you are asking for and your reason.
        </span>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <RegStat label="Day" tint="bg-brand-500/8" value={day ? formatIstDate(day) : '—'} hint={day ? day.format('dddd') : undefined} />
        <RegStat label="What is missing" tint="bg-warning-500/10" value={REG_TYPE_LABEL[type] ?? type} />
        <RegStat label="Check-in" tint="bg-sky-500/8" value={needIn ? (times.checkInTime ? times.checkInTime.format('hh:mm A') : 'not set') : 'recorded'} />
        <RegStat label="Check-out" tint="bg-violet-500/8" value={needOut ? (times.checkOutTime ? times.checkOutTime.format('hh:mm A') : 'not set') : 'recorded'} />
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-1.5 type-caption text-subtle">
        If your manager approves, that day is written as
        <Tag tone={dayType === 'PRESENT' ? 'green' : 'gold'}>{dayType === 'PRESENT' ? 'Present (full day)' : 'Half day'}</Tag>
        and stops counting as absent.
      </div>
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}
        onValuesChange={(changed: any) => {
          if (changed && 'type' in changed) setType(changed.type);
          if (changed && 'logDate' in changed) setDay(changed.logDate ?? null);
          if (changed && 'status' in changed) setDayType(changed.status);
          if (changed && ('checkInTime' in changed || 'checkOutTime' in changed)) setTimes((t) => ({ ...t, ...changed }));
        }}
        initialValues={{ type: DEFAULT_REG_TYPE, status: 'PRESENT', logDate: dayjs().subtract(1, 'day') }}>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="logDate" label="Date" rules={[{ required: true }]}>
              <DatePicker className="w-full" disabledDate={(d) => d && (d.isAfter(dayjs(), 'day') || d.isBefore(dayjs().subtract(7, 'day'), 'day'))} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="status" label="Day type" rules={[{ required: true }]}>
              <Select options={[{ value: 'PRESENT', label: 'Present (full day)' }, { value: 'HALF_DAY', label: 'Half day' }]} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="type" label="What did you miss?" rules={[{ required: true }]}>
          <Select options={[
            { value: 'MISSING_BOTH', label: 'Forgot both check-in & check-out' },
            { value: 'MISSING_CHECK_IN', label: 'Forgot check-in' },
            { value: 'MISSING_CHECK_OUT', label: 'Forgot check-out' },
          ]} />
        </Form.Item>
        <Row gutter={12}>
          {needIn && <Col xs={24} sm={12}><Form.Item name="checkInTime" label="Check-in time" rules={[{ required: true, message: 'Enter check-in time' }]}><TimePicker format="hh:mm A" style={{ width: '100%' }} /></Form.Item></Col>}
          {needOut && <Col xs={24} sm={12}><Form.Item name="checkOutTime" label="Check-out time" rules={[{ required: true, message: 'Enter check-out time' }]}><TimePicker format="hh:mm A" style={{ width: '100%' }} /></Form.Item></Col>}
        </Row>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'A reason is required' }]}>
          <Input.TextArea rows={2} placeholder="e.g. Phone died, was at a retailer site, network issue…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Manager side: review the team's pending requests ──────────────────────────
export function RegularizationReviewCard() {
  const [reviewFor, setReviewFor] = useState<any | null>(null);
  const { data: team, isLoading } = useQuery({
    queryKey: ['reg-team'],
    // Only PENDING — the card is a review queue, matching the nav badge.
    queryFn: () => request<any[]>({ url: '/attendance-regularizations', params: { status: 'PENDING' } }),
  });
  /* The approvals hub links the ONE request it means (?focus=…). There is nothing to expand here, so
     the honest equivalent of "open it" is the review dialog the manager was heading for anyway. */
  useFocusRow(team, { onFocus: (r: any) => setReviewFor(r) });

  // Where each staff member works, for the chips under their name. Cached for the session and shared
  // with every other screen that draws a territory.
  const terr = useStaffTerritories();

  const cols = [
    // The number the request is known by everywhere else — see the requester's table above.
    { title: 'Request', dataIndex: 'docNo', width: 150, render: (v: string | null) => (v ? <CodeChip>{v}</CodeChip> : <span className="text-subtle">—</span>) },
    { title: 'Date', dataIndex: 'logDate', width: 110, render: (v: string) => <span className="whitespace-nowrap">{formatIstDate(v)}</span> },
    /*
     * The role was a grey caption UNDER the name — two lines of text to read where a coloured pill
     * beside the name is a column you can scan. The line it vacates carries where the person works,
     * as the same level-coloured chips every customer and staff list in the app now uses.
     */
    { title: 'Staff', render: (_: any, r: any) => (
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-1.5">
          {/* The name opens the person, as it does in every other register — an approver deciding
              whose day to write often wants their book and their reporting line first. A missing
              name says "Field staff": a row id is not a person. */}
          <UserLink id={r.userId} role={r.staffRole} className="truncate font-medium">
            {r.staffName ?? 'Field staff'}
          </UserLink>
          <RoleBadge role={r.staffRole} />
        </div>
        <div className="mt-0.5"><TerritoryNodeChips nodes={terr.booksOf(r.userId)} max={2} hideWhenEmpty /></div>
      </div>
    ) },
    { title: 'Missed', dataIndex: 'type', render: (v: string) => REG_TYPE_LABEL[v] ?? v },
    { title: 'Requested', render: (_: any, r: any) => requestedCell(r) },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true },
    { title: '', width: 110, render: (_: any, r: any) => <Button size="sm" variant="primary" icon={<Check size={16} />} onClick={() => setReviewFor(r)}>Review</Button> },
  ];

  return (
    <Card title="Requests to review">
      <Table rowKey="id" size="small" loading={isLoading} dataSource={team ?? []} columns={cols as any}
        pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        locale={{ emptyText: <EmptyState title="No pending regularization requests" /> }} />
      <ReviewRegularizationModal req={reviewFor} onClose={() => setReviewFor(null)} />
    </Card>
  );
}

function ReviewRegularizationModal({ req, onClose }: { req: any | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>('PRESENT');
  const [checkInTime, setCheckInTime] = useState<Dayjs | null>(null);
  const [checkOutTime, setCheckOutTime] = useState<Dayjs | null>(null);
  const [note, setNote] = useState('');

  // Seed from the request as soon as it changes (no flash; no dependence on open animation).
  useEffect(() => {
    if (!req) return;
    setStatus(req.requestedStatus ?? 'PRESENT');
    setCheckInTime(req.requestedCheckInAt ? dayjs(req.requestedCheckInAt) : null);
    setCheckOutTime(req.requestedCheckOutAt ? dayjs(req.requestedCheckOutAt) : null);
    setNote('');
  }, [req?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const reviewMut = useConfirmedMutation((approve) => ({ title: approve ? "Approve this regularisation?" : "Reject this regularisation?", content: approve ? "The attendance day is marked as requested." : "The attendance day is left as it is and your note goes back to the staff member.", okText: approve ? "Yes, approve" : "Yes, reject", danger: !approve }), {
    mutationFn: (approve: boolean) => {
      const combine = (t: Dayjs | null) => (t && req) ? dayjs(req.logDate).hour(t.hour()).minute(t.minute()).second(0).toISOString() : undefined;
      return request({
        url: `/attendance-regularizations/${req.id}/review`, method: 'POST',
        data: approve
          ? { approve: true, status, checkInAt: combine(checkInTime), checkOutAt: combine(checkOutTime) }
          : { approve: false, note },
      });
    },
    onSuccess: (_d, approve) => {
      message.success(approve ? 'Approved — attendance marked' : 'Request rejected');
      qc.invalidateQueries({ queryKey: ['reg-team'] });
      qc.invalidateQueries({ queryKey: ['attendance'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal open={!!req} onOpenChange={(o) => { if (!o) onClose(); }}
      // Titled by the request's own number where it has one, then whose day it is — the same handle
      // the approvals hub and the bell use for it. A row id names the record to nobody.
      title={req
        ? ['Review', req.docNo, req.staffName ?? 'Field staff', formatIstDate(req.logDate)].filter(Boolean).join(' · ')
        : 'Review'}
      footer={[
        <Button key="reject" variant="danger" icon={<X size={16} />} loading={reviewMut.isPending}
          onClick={() => { if (!note.trim()) { message.error('Add a note explaining the rejection'); return; } reviewMut.mutate(false); }}>Reject</Button>,
        <Button key="approve" variant="primary" icon={<Check size={16} />} loading={reviewMut.isPending}
          onClick={() => reviewMut.mutate(true)}>Approve &amp; mark</Button>,
      ]}>
      {req && (
        <>
          <Descriptions bordered size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Staff">{req.staffName ?? 'Field staff'}{req.staffRole ? ` · ${req.staffRole}` : ''}</Descriptions.Item>
            <Descriptions.Item label="Date">{formatIstDate(req.logDate)}</Descriptions.Item>
            <Descriptions.Item label="Missed">{REG_TYPE_LABEL[req.type] ?? req.type}</Descriptions.Item>
            <Descriptions.Item label="Reason">{req.reason}</Descriptions.Item>
          </Descriptions>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Adjust if needed, then approve to mark the attendance:</Typography.Text>
          <Row gutter={12} style={{ marginTop: 8 }}>
            <Col span={8}><div style={{ fontSize: 12, marginBottom: 4 }}>Day type</div>
              <Select className="w-full" value={status} onChange={setStatus} options={[{ value: 'PRESENT', label: 'Present' }, { value: 'HALF_DAY', label: 'Half day' }]} /></Col>
            <Col span={8}><div style={{ fontSize: 12, marginBottom: 4 }}>Check-in</div>
              <TimePicker format="hh:mm A" style={{ width: '100%' }} value={checkInTime} onChange={setCheckInTime} /></Col>
            <Col span={8}><div style={{ fontSize: 12, marginBottom: 4 }}>Check-out</div>
              <TimePicker format="hh:mm A" style={{ width: '100%' }} value={checkOutTime} onChange={setCheckOutTime} /></Col>
          </Row>
          <div style={{ fontSize: 12, margin: '12px 0 4px' }}>Note <span style={{ color: '#999' }}>(required to reject)</span></div>
          <Input.TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional on approve; required on reject" />
        </>
      )}
    </Modal>
  );
}
